import React, { createContext, useContext, useState, useEffect } from "react";

// Create the context
const OnlineStatusContext = createContext({
  isOnline: true,
  lastOnlineTime: null,
  connectionQuality: "good",
});

// Hook to use the online status
export const useOnlineStatus = () => {
  const context = useContext(OnlineStatusContext);
  if (!context) {
    throw new Error("useOnlineStatus must be used within OnlineStatusProvider");
  }
  return context;
};

// Provider component
export const OnlineStatusProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastOnlineTime, setLastOnlineTime] = useState(Date.now());
  const [connectionQuality, setConnectionQuality] = useState("good");

  useEffect(() => {
    const handleOnline = () => {
      console.log("🟢 Network: Online");
      setIsOnline(true);
      setLastOnlineTime(Date.now());
      setConnectionQuality("good");

      // Show a brief success notification
      if (window.lastOfflineTime) {
        const offlineDuration = Math.floor(
          (Date.now() - window.lastOfflineTime) / 1000
        );
        console.log(`✅ Back online after ${offlineDuration} seconds`);
      }
    };

    const handleOffline = () => {
      console.log("🔴 Network: Offline");
      setIsOnline(false);
      window.lastOfflineTime = Date.now();
      setConnectionQuality("offline");
    };

    // Connection quality detection (experimental)
    const checkConnectionQuality = () => {
      if (!navigator.onLine) {
        setConnectionQuality("offline");
        return;
      }

      // Use Network Information API if available
      if ("connection" in navigator) {
        const connection =
          navigator.connection ||
          navigator.mozConnection ||
          navigator.webkitConnection;

        if (connection) {
          const effectiveType = connection.effectiveType;

          if (effectiveType === "slow-2g" || effectiveType === "2g") {
            setConnectionQuality("poor");
          } else if (effectiveType === "3g") {
            setConnectionQuality("fair");
          } else {
            setConnectionQuality("good");
          }
        }
      }
    };

    // Add event listeners
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Check connection quality periodically
    const qualityInterval = setInterval(checkConnectionQuality, 30000); // Every 30 seconds
    checkConnectionQuality(); // Initial check

    // Cleanup
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(qualityInterval);
    };
  }, []);

  const value = {
    isOnline,
    lastOnlineTime,
    connectionQuality,
  };

  return (
    <OnlineStatusContext.Provider value={value}>
      {children}
    </OnlineStatusContext.Provider>
  );
};

export default OnlineStatusContext;
